import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Logo } from '@/components/brand/Logo';

const schema = z.object({
  email: z.string().min(1, 'Email wajib diisi.').email('Format email tidak valid.'),
});

/**
 * Meminta tautan setel ulang kata sandi.
 *
 * Halaman ini selalu menampilkan pesan berhasil, ada atau tidak akunnya - 
 * mengikuti perilaku endpoint-nya. Kalau tampilan membedakan keduanya,
 * halaman ini berubah menjadi alat untuk menebak email mana yang terdaftar.
 */
export const ForgotPasswordPage = () => {
  const [terkirim, setTerkirim] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const form = useForm({ resolver: zodResolver(schema), mode: 'onBlur' });

  const onSubmit = async (values) => {
    try {
      const hasil = await api.post('/auth/forgot-password', values);
      // Respons API selalu terbungkus { data }.
      setDevToken(hasil?.data?.devToken ?? null);
      setTerkirim(true);
    } catch (err) {
      form.setError('email', {
        message:
          err instanceof ApiError ? err.message : 'Gagal menghubungi server. Coba lagi.',
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--surface-canvas)] px-4 py-10">
      <Link to="/" className="mb-7">
        <Logo />
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-6 shadow-sm sm:p-7">
        {terkirim ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-success-50 text-success-600 dark:bg-success-500/15">
              <MailCheck className="size-6" aria-hidden="true" />
            </div>
            <h1 className="text-lg font-bold text-[var(--text-primary)]">Cek email Anda</h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Kalau email tersebut terdaftar, tautan untuk menyetel ulang kata sandi sudah
              dikirim. Tautannya berlaku satu jam.
            </p>

            {/*
              Pengiriman email belum tersambung. Selama itu, di lingkungan
              pengembangan tokennya ditampilkan langsung supaya alur ini bisa
              dicoba sampai tuntas. Di produksi ruas ini tidak pernah terisi.
            */}
            {devToken && (
              <div className="mt-5 rounded-lg border border-warning-200 bg-warning-50 p-3 text-left dark:border-warning-500/30 dark:bg-warning-500/10">
                <p className="text-xs font-bold text-warning-800 dark:text-warning-500">
                  Mode pengembangan - SMTP belum aktif
                </p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  Pakai tautan ini untuk melanjutkan:
                </p>
                <Link
                  to={`/reset-sandi?token=${devToken}`}
                  className="mt-2 block font-mono text-xs break-all text-brand-600 underline dark:text-brand-400"
                >
                  /reset-sandi?token={devToken}
                </Link>
              </div>
            )}

            <Link
              to="/masuk"
              className="mt-6 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Kembali ke halaman masuk
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-bold text-[var(--text-primary)]">Lupa kata sandi</h1>
            <p className="mt-1.5 mb-6 text-sm text-[var(--text-secondary)]">
              Masukkan email akun Anda. Kami kirimkan tautan untuk membuat kata sandi baru.
            </p>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <Field label="Email" required error={form.formState.errors.email?.message}>
                {(props) => (
                  <Input
                    {...props}
                    {...form.register('email')}
                    type="email"
                    placeholder="nama@perusahaan.com"
                    autoComplete="email"
                    autoFocus
                    error={form.formState.errors.email}
                  />
                )}
              </Field>

              <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
                Kirim tautan setel ulang
              </Button>
            </form>

            <Link
              to="/masuk"
              className="mt-6 flex items-center justify-center gap-1.5 text-sm font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Kembali ke halaman masuk
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
